import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Button, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  ButtonGroup, Chip, SelectChangeEvent
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:17001';

// Types
interface Scenario {
  id: string;
  name: string;
  strategy: string;
  crewSize: number;
  fixedEvents: number;
  totalCost: number;
  totalDowntime: number;
  efficiency: number;
  roi: number;
  risk: 'Low' | 'Medium' | 'High';
  status: 'Recommended' | 'Alternative' | 'Not Recommended';
}

interface MultiScenarioViewProps {
  title?: string;
}

function MultiScenarioView({ title = "Multi-Scenario Analysis" }: MultiScenarioViewProps) {
  // State
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'mock' | 'live'>('mock');
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);

  // Load scenarios from API
  useEffect(() => {
    async function loadScenarios() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/api/modeling/scenarios?mode=${mode}`);
        const scenariosData = await response.json();
        setScenarios(scenariosData);
        if (scenariosData.length > 0) {
          setSelectedScenario(scenariosData[0]);
        }
      } catch (error) {
        console.error('Error loading scenarios:', error);
        setError('Error loading scenarios');
      } finally {
        setLoading(false);
      }
    }
    loadScenarios();
  }, [mode]);

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low': return 'success';
      case 'Medium': return 'warning';
      case 'High': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Recommended': return 'success';
      case 'Alternative': return 'warning';
      case 'Not Recommended': return 'error';
      default: return 'default';
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {/* Header with Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}</Typography>
        <ButtonGroup variant="outlined" size="small">
          <Button
            variant={mode === 'mock' ? 'contained' : 'outlined'}
            onClick={() => setMode('mock')}
          >
            Mock Data
          </Button>
          <Button
            variant={mode === 'live' ? 'contained' : 'outlined'}
            onClick={() => setMode('live')}
          >
            Live Data
          </Button>
        </ButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {/* Scenarios Table */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Scenario Comparison</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Scenario</TableCell>
                    <TableCell>Strategy</TableCell>
                    <TableCell>Crew Size</TableCell>
                    <TableCell>Fixed Events</TableCell>
                    <TableCell>Total Cost ($)</TableCell>
                    <TableCell>Downtime (h)</TableCell>
                    <TableCell>Efficiency (%)</TableCell>
                    <TableCell>ROI (%)</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scenarios.map((scenario) => (
                    <TableRow
                      key={scenario.id}
                      hover
                      onClick={() => setSelectedScenario(scenario)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: selectedScenario?.id === scenario.id ? 'action.hover' : 'transparent',
                      }}
                    >
                      <TableCell>{scenario.name}</TableCell>
                      <TableCell>{scenario.strategy}</TableCell>
                      <TableCell>{scenario.crewSize}</TableCell>
                      <TableCell>{scenario.fixedEvents}</TableCell>
                      <TableCell>${scenario.totalCost.toLocaleString()}</TableCell>
                      <TableCell>{scenario.totalDowntime}</TableCell>
                      <TableCell>{scenario.efficiency}%</TableCell>
                      <TableCell>{scenario.roi}%</TableCell>
                      <TableCell>
                        <Chip 
                          label={scenario.risk} 
                          color={getRiskColor(scenario.risk) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={scenario.status} 
                          color={getStatusColor(scenario.status) as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Selected Scenario Details */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Selected Scenario</Typography>
            {selectedScenario ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedScenario.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedScenario.strategy}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Total Cost</Typography>
                    <Typography variant="h6" color="primary">
                      ${selectedScenario.totalCost.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Downtime</Typography>
                    <Typography variant="h6" color="error">
                      {selectedScenario.totalDowntime}h
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Efficiency</Typography>
                    <Typography variant="h6" color="success.main">
                      {selectedScenario.efficiency}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">ROI</Typography>
                    <Typography variant="h6" color="info.main">
                      {selectedScenario.roi}%
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Chip 
                    label={`Risk: ${selectedScenario.risk}`} 
                    color={getRiskColor(selectedScenario.risk) as any}
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Chip 
                    label={selectedScenario.status} 
                    color={getStatusColor(selectedScenario.status) as any}
                  />
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" fullWidth sx={{ mb: 1 }}>
                    Deploy Scenario
                  </Button>
                  <Button variant="outlined" fullWidth>
                    Export Report
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a scenario to view details
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Charts */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Scenario Analysis</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Cost Comparison</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={scenarios}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="totalCost" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Efficiency vs ROI</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={scenarios}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="efficiency" stroke="#8884d8" />
                    <Line type="monotone" dataKey="roi" stroke="#82ca9d" />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MultiScenarioView; 